/**
 * Universal Number Formatting Utilities
 *
 * These utilities parse and format numbers without any locale assumptions.
 * They preserve whatever separators the editor uses and animate accordingly.
 */

export interface NumberFormat {
  thousandSeparator: string;
  decimalSeparator: string;
}

/**
 * Parse any number format and return the numeric value
 * Only parses strings that are purely numbers (with separators)
 * Returns NaN for mixed text like "4.2/5" or "4.2kg"
 */
export function parseAnyNumber(text: string): number {
  if (!text || typeof text !== 'string') return NaN;

  const cleaned = text.trim();

  // If it's already a clean number, return it
  if (/^\d+\.?\d*$/.test(cleaned)) {
    return parseFloat(cleaned);
  }

  // Check if this contains non-number characters that indicate it's not a pure number
  // Like /, %, letters, etc. (but allow spaces, dots, commas as separators)
  if (/[^\d\s.,-]/.test(cleaned)) {
    return NaN; // This is mixed text, not a pure number
  }

  const separators = cleaned.match(/[^\d]/g) || [];
  if (separators.length === 0) {
    return parseFloat(cleaned);
  }

  // Check for invalid patterns like multiple different decimal separators
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  const hasSpace = cleaned.includes(' ');

  if (hasComma && hasDot) {
    // Both comma and dot - validate this is a proper format
    const commaIndex = cleaned.lastIndexOf(',');
    const dotIndex = cleaned.lastIndexOf('.');

    if (commaIndex > dotIndex) {
      // Comma after dot - check if it's valid like "1.234,56"
      const afterComma = cleaned.substring(commaIndex + 1);
      if (!/^\d{1,3}$/.test(afterComma.trim())) {
        return NaN; // Invalid format
      }
    } else {
      // Dot after comma - check if it's valid like "1,234.56"
      const afterDot = cleaned.substring(dotIndex + 1);
      if (!/^\d{1,3}$/.test(afterDot.trim())) {
        return NaN; // Invalid format
      }
    }
  }

  // Check for patterns like "18,00.10 90" which are invalid
  if (hasSpace) {
    const spaceParts = cleaned.split(/\s+/);
    if (spaceParts.length > 2) {
      return NaN; // Too many space-separated parts
    }
    // Check if any part after space looks like it should be part of the main number
    if (spaceParts.length === 2) {
      const secondPart = spaceParts[1];
      if (secondPart.includes('.') || secondPart.includes(',')) {
        return NaN; // Second part has decimal indicators - this is malformed
      }
    }
  }

  let decimalIndex = -1;
  let decimalSeparator = '';

  // Find the rightmost valid decimal separator
  for (let i = cleaned.length - 1; i >= 0; i--) {
    const char = cleaned[i];
    if (char === '.' || char === ',') {
      const afterSeparator = cleaned.substring(i + 1).trim();
      if (/^\d{1,3}$/.test(afterSeparator)) {
        decimalIndex = i;
        decimalSeparator = char;
        break;
      }
    }
  }

  let result = '';

  if (decimalIndex >= 0) {
    const beforeDecimal = cleaned.substring(0, decimalIndex);
    const afterDecimal = cleaned.substring(decimalIndex + 1);

    const integerPart = beforeDecimal.replace(/[^\d]/g, '');
    const decimalPart = afterDecimal.replace(/[^\d]/g, '');

    result = integerPart + '.' + decimalPart;
  } else {
    result = cleaned.replace(/[^\d]/g, '');
  }

  const finalNumber = parseFloat(result);
  return isNaN(finalNumber) ? NaN : finalNumber;
}

/**
 * Detect the number format used in the original text
 */
export function detectNumberFormat(text: string): NumberFormat {
  if (!text || typeof text !== 'string') {
    return { thousandSeparator: '', decimalSeparator: '.' };
  }

  const cleaned = text.trim();

  const separators = cleaned.match(/[^\d]/g) || [];
  if (separators.length === 0) {
    return { thousandSeparator: '', decimalSeparator: '.' };
  }

  let decimalSeparator = '.';
  let thousandSeparator = '';

  for (let i = cleaned.length - 1; i >= 0; i--) {
    const char = cleaned[i];
    if (char === '.' || char === ',') {
      const afterSeparator = cleaned.substring(i + 1);
      if (/^\d{1,3}$/.test(afterSeparator)) {
        decimalSeparator = char;
        break;
      }
    }
  }

  const uniqueSeparators = [...new Set(separators)];
  for (const sep of uniqueSeparators) {
    if (sep !== decimalSeparator) {
      thousandSeparator = sep;
      break;
    }
  }

  return { thousandSeparator, decimalSeparator };
}

export function getDecimalPlaces(text: string): number {
  if (!text || typeof text !== 'string') return 0;

  const cleaned = text.trim();

  for (let i = cleaned.length - 1; i >= 0; i--) {
    const char = cleaned[i];
    if (char === '.' || char === ',') {
      const afterSeparator = cleaned.substring(i + 1);
      if (/^\d{1,3}$/.test(afterSeparator)) {
        return afterSeparator.length;
      }
    }
  }

  return 0;
}

export function separateNumbers(text: string): string[] {
  if (!text) return [];

  // First, handle obvious malformed patterns by pre-splitting them
  // Split on patterns that are clearly separate elements
  let preprocessed = text;

  // Split on patterns like "18,00.10 90" where there are conflicting separators
  // This regex finds patterns like "number,number.number space" which are malformed
  preprocessed = preprocessed.replace(/(\d+[,.]\d+[,.]\d+)(\s+\d+)/g, '$1 $2');

  // More precise pattern for numbers with separators
  // This matches complete number patterns but is stricter about validation
  const parts: string[] = [];
  const numberPattern = /\d+(?:[\s.,-]\d+)*(?:[\s.,-]\d{1,3})?/g;
  let lastIndex = 0;
  let match;

  while ((match = numberPattern.exec(preprocessed)) !== null) {
    // Add text before the number
    if (match.index > lastIndex) {
      parts.push(preprocessed.substring(lastIndex, match.index));
    }

    const numberCandidate = match[0];

    // Validate that this is actually a proper number before treating it as one
    if (!isNaN(parseAnyNumber(numberCandidate)) && parseAnyNumber(numberCandidate) !== 0) {
      parts.push(numberCandidate);
    } else {
      // If it's not a valid number, split it further
      // This handles cases like "18,00.10 90" by breaking it down
      const invalidNumber = numberCandidate;
      const subParts = invalidNumber.split(/(\s+)/);

      for (const subPart of subParts) {
        if (subPart.trim() && !isNaN(parseAnyNumber(subPart))) {
          parts.push(subPart);
        } else if (subPart.trim()) {
          parts.push(subPart);
        }
      }
    }

    lastIndex = numberPattern.lastIndex;
  }

  if (lastIndex < preprocessed.length) {
    parts.push(preprocessed.substring(lastIndex));
  }

  return parts.filter((part) => part.length > 0);
}

export function hasAnimatableNumbers(text: string): boolean {
  if (!text) return false;
  const parts = separateNumbers(text);
  return parts.some((part) => !isNaN(parseAnyNumber(part)) && parseAnyNumber(part) !== 0);
}

export function removeSuffixedStrings(parts: string[]): string[] {
  return parts.map((part) => {
    return part.replace(/\s*(st|nd|rd|th|%|€|\$|£|kr|€|SEK|NOK|DKK|EUR|USD|GBP)\s*$/i, '');
  });
}

export const parseLocalizedNumber = parseAnyNumber;
export const getDisplayFormat = detectNumberFormat;

export const getLocaleNumberFormat = (locale?: string): NumberFormat => {
  return { thousandSeparator: ',', decimalSeparator: '.' };
};
